import classNames from "classnames";
import React from "react";
import { getFileName } from "src/utils/common";
import "../../../scss/custom/page/_flight-simulator.scss";
import { isEmpty } from "lodash";
import { FORM_NAME } from "../Modal/ModalUploadFile3DOF";
import { useRecoilValue } from "recoil";
import { uploadingFile } from "src/recoil/atoms/global";
import { Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import TooltipWrapper from "src/components/Common/TooltipWrapper";
import { ButtonErrorWarning } from "../Button";

const ParamsInput3DoFTableItem = ({ file, index, name }) => {
  const fileList = Array.isArray(file) ? file : file ? [file] : [];
  const fileNames = fileList
    .map((f) => (f?.name ? getFileName(f.name) : getFileName(f)))
    .join(', ');

  return (
    <div
      className={classNames("p-2 d-flex align-items-center justify-content-center border height-by-px-48")}
      key={index}
    >
      {fileList.length > 0 ? (
        <TooltipWrapper
          id={`file-${name}-${index}`}
          tooltipText={fileNames}
          checkOverflow={true}
        >
          <p className="text-muted fw-bold fs-15 mb-0 text-truncate">
            {fileNames}
          </p>
        </TooltipWrapper>
      ) : (
        <span className="d-block text-center fs-2 fw-normal">-</span>
      )}
    </div>
  );
};

const ParamsInput3DoFTable = ({ paramsInput, setConfirmDelete, setItemDelete, isNewVersion, findErrorForParamInput = null, isDebrisImpact = false }) => {
  const globalUploadingField = useRecoilValue(uploadingFile);
  const { t } = useTranslation("flight-simulator");
  const getHeaderTitles = () => {
    if (isNewVersion) {
      return [
        `${t("new.parameter_json")} json`,
        `${t("new.aero_coefficients")}`,
        `${t("new.wind_model")}`,
        `${t("new.thrust_profile")}`,
        ...(isDebrisImpact ? [`${t("new.reference_trajectory_files")}`] : []),
        `${t("new.kml_line_th")}`,
      ];
    }
    return [
      `${t("new.parameter_json")} json`,
      `${t("new.aero_coefficients_1")}`,
      `${t("new.aero_coefficients_2")}`,
      `${t("new.wind_model")}`,
      `${t("new.thrust_profile")}`,
      ...(isDebrisImpact ? [`${t("new.reference_trajectory_files")}`] : []),
      `${t("new.kml_line_th")}`,
    ];
  };

  return (
    <div
      className={classNames("table-responsive border mb-3 p-t-12 p-x-12 p-b-12", {
        "p-b-22": isEmpty(paramsInput),
      })}
    >
      {globalUploadingField ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner color="secondary" />
        </div>
      ) : (
        <table className="table mb-0 params-input-3dof-table">
          <thead>
            <tr>
              {getHeaderTitles()?.map((title, index) => (
                <th key={index} className="border-0 text-nowrap important-p-t-10 important-p-x-10 important-p-b-0">
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!isEmpty(paramsInput) &&
              paramsInput?.map((param, index) => (
                <tr key={index}>
                  <td className="border-0 min-width-by-px-240 width-by-percent-25 important-p-a-10">
                    <div className="card mb-0 shadow-none border m-b-7">
                      <div className="p-2 d-flex align-items-center height-by-px-48">
                        <div
                          className={classNames("d-flex align-items-center", {
                            "me-0": !!findErrorForParamInput?.(param),
                            "me-2": !findErrorForParamInput?.(param),
                          })}
                        >
                          <i
                            className="bi bi-trash text-danger f-s-24 user-select-pointer"
                            role="button"
                            onClick={() => {
                              setItemDelete(index);
                              setConfirmDelete(true);
                            }}
                          />
                        </div>
                        {!!findErrorForParamInput?.(param) && (
                          <div className="important-p-x-6">
                            <ButtonErrorWarning
                              isShow={!!findErrorForParamInput?.(param)}
                              errorContent={findErrorForParamInput?.(param)}
                              id={`param-input-error-${index}`}
                              iconClassName="cursor-pointer"
                            />
                          </div>
                        )}
                        <div className="row align-items-center">
                          <TooltipWrapper
                            id={`file-${FORM_NAME.PARAMETER_JSON}-${index}`}
                            tooltipText={
                              param?.[FORM_NAME.PARAMETER_JSON]?.name
                                  ? getFileName(param?.[FORM_NAME.PARAMETER_JSON]?.name)
                                  : getFileName(param?.[FORM_NAME.PARAMETER_JSON])
                            }
                            checkOverflow={true}
                          >
                            <p className="text-muted fw-bold fs-15 mb-0 text-truncate">
                              {param?.[FORM_NAME.PARAMETER_JSON]?.name
                                ? getFileName(param?.[FORM_NAME.PARAMETER_JSON]?.name)
                                : getFileName(param?.[FORM_NAME.PARAMETER_JSON])}
                            </p>
                            </TooltipWrapper>
                        </div>
                      </div>
                    </div>
                  </td>
                  {isNewVersion ? (
                    <td className="border-0 min-width-by-px-240 width-by-percent-25 important-p-a-10">
                      <ParamsInput3DoFTableItem
                        file={param?.[FORM_NAME.AERO_COEFFICIENTS]}
                        index={index}
                        name={FORM_NAME.AERO_COEFFICIENTS}
                      />
                    </td>
                  ) : (
                    <>
                      <td className="border-0 min-width-by-px-240 width-by-percent-20 important-p-a-10">
                        <ParamsInput3DoFTableItem
                          file={param?.[FORM_NAME.AERO_COEFFICIENTS_1]}
                          index={index}
                          name={FORM_NAME.AERO_COEFFICIENTS_1}
                        />
                      </td>
                      <td className="border-0 min-width-by-px-240 width-by-percent-20 important-p-a-10">
                        <ParamsInput3DoFTableItem
                          file={param?.[FORM_NAME.AERO_COEFFICIENTS_2]}
                          index={index}
                          name={FORM_NAME.AERO_COEFFICIENTS_2}
                        />
                      </td>
                    </>
                  )}
                  <td className="border-0 min-width-by-px-240 width-by-percent-25 important-p-a-10">
                    <ParamsInput3DoFTableItem file={param?.[FORM_NAME.WIND_MODEL]} index={index} name={FORM_NAME.WIND_MODEL} />
                  </td>
                  <td className="border-0 min-width-by-px-240 width-by-percent-25 important-p-a-10">
                    <ParamsInput3DoFTableItem file={param?.[FORM_NAME.THRUST_PROFILE]} index={index} name={FORM_NAME.THRUST_PROFILE} />
                  </td>
                  {isDebrisImpact && (
                    <td className="border-0 min-width-by-px-240 width-by-percent-20 important-p-a-10">
                      <ParamsInput3DoFTableItem file={param?.[FORM_NAME.REFERENCE_TRAJECTORY]} index={index} name={FORM_NAME.REFERENCE_TRAJECTORY} />
                    </td>
                  )}
                  <td className="border-0 min-width-by-px-240 width-by-percent-20 important-p-a-10">
                    <ParamsInput3DoFTableItem file={param?.[FORM_NAME.KML_LINE]} index={index} name={FORM_NAME.KML_LINE} />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ParamsInput3DoFTable;
